import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../Header';
import Footer from '../Footer';
import LoginModal from '../LoginModal';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Users, Heart, MessageCircle, Share2, Send, Image, 
  Briefcase, MapPin, Clock, Building2, MoreHorizontal,
  ThumbsUp, Bookmark, X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FeedPage = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [newPostContent, setNewPostContent] = useState('');
  const [isPosting, setIsPosting] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  useEffect(() => {
    if (isAuthenticated && currentUser?.id) {
      fetchUserProfile(currentUser.id);
    }
    fetchPosts();
  }, [currentUser, isAuthenticated]);

  const fetchUserProfile = async (userId) => {
    try {
      const response = await axios.get(`${API}/connect/profiles/user/${userId}`);
      if (response.data.exists) {
        setUserProfile(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar perfil do usuário:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const params = userProfile?.profile_id ? `?viewer_id=${userProfile.profile_id}` : '';
      const response = await axios.get(`${API}/connect/posts${params}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Erro ao carregar publicações:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostContent.trim()) {
      toast.error('Escreva algo para publicar');
      return;
    }

    if (!userProfile?.profile_id) {
      toast.error('Crie seu perfil primeiro para publicar');
      navigate('/connect/create-profile');
      return;
    }

    setIsPosting(true);
    try {
      await axios.post(`${API}/connect/posts?author_id=${userProfile.profile_id}`, {
        content: newPostContent,
        post_type: 'post',
        media_urls: []
      });
      toast.success('Post publicado!');
      setNewPostContent('');
      setShowCreatePost(false);
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao publicar');
    } finally {
      setIsPosting(false);
    }
  };

  const handleLike = async (postId) => {
    if (!userProfile?.profile_id) {
      toast.error('Faça login para curtir');
      return;
    }

    try {
      const response = await axios.post(`${API}/connect/posts/${postId}/like?profile_id=${userProfile.profile_id}`);
      
      // Update local state
      setPosts(posts.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            likes_count: response.data.likes_count,
            is_liked: response.data.action === 'liked'
          };
        }
        return post;
      }));
    } catch (error) {
      toast.error('Erro ao curtir');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (hours < 1) return 'Agora';
    if (hours < 24) return `${hours}h`;
    if (days < 7) return `${days}d`;
    return date.toLocaleDateString('pt-BR');
  };

  return (
    <div className="min-h-screen bg-black">
      <Header onLoginClick={() => setIsLoginModalOpen(true)} />
      <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
      
      <section className="pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-2xl">
          {/* Create Post */}
          {isAuthenticated && userProfile && (
            <Card className="bg-zinc-900 border-zinc-800 mb-6">
              <CardContent className="p-4">
                {!showCreatePost ? (
                  <div 
                    className="flex items-center gap-4 cursor-pointer"
                    onClick={() => setShowCreatePost(true)}
                  >
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">
                      {userProfile.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div className="flex-1 bg-zinc-800 rounded-full px-4 py-3 text-gray-400 hover:bg-zinc-700 transition-colors">
                      Compartilhe algo com a comunidade BIM...
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold flex-shrink-0">
                        {userProfile.name?.charAt(0)?.toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <textarea
                          value={newPostContent}
                          onChange={(e) => setNewPostContent(e.target.value)}
                          placeholder="O que você quer compartilhar?"
                          className="w-full bg-transparent text-white resize-none focus:outline-none text-lg"
                          rows={4}
                          autoFocus
                          data-testid="post-content-input"
                        />
                      </div>
                      <button 
                        onClick={() => setShowCreatePost(false)}
                        className="text-gray-500 hover:text-white"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" className="text-gray-400">
                          <Image className="w-5 h-5 mr-2" />
                          Foto
                        </Button>
                      </div>
                      <Button 
                        onClick={handleCreatePost}
                        disabled={isPosting || !newPostContent.trim()}
                        className="bg-blue-600 hover:bg-blue-700"
                        data-testid="publish-post-btn"
                      >
                        {isPosting ? 'Publicando...' : 'Publicar'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Feed */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
              <p className="text-gray-400 mt-4">Carregando feed...</p>
            </div>
          ) : posts.length === 0 ? (
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8 text-center">
                <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Nenhum post ainda</h3>
                <p className="text-gray-400">Seja o primeiro a compartilhar algo!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {posts.map((post) => (
                <Card 
                  key={post.id} 
                  className="bg-zinc-900 border-zinc-800 hover:border-zinc-700 transition-colors"
                  data-testid={`post-${post.id}`}
                >
                  <CardContent className="p-4">
                    {/* Post Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div 
                        className="flex items-center gap-3 cursor-pointer"
                        onClick={() => navigate(`/connect/profile/${post.author_id}`)}
                      >
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold">
                          {post.author_photo ? (
                            <img src={post.author_photo} alt={post.author_name} className="w-full h-full rounded-full object-cover" />
                          ) : (
                            post.author_name?.charAt(0)?.toUpperCase()
                          )}
                        </div>
                        <div>
                          <h4 className="font-semibold text-white hover:text-blue-400 transition-colors">
                            {post.author_name}
                          </h4>
                          <p className="text-sm text-gray-500">{post.author_headline}</p>
                          <p className="text-xs text-gray-600">{formatDate(post.created_at)}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-gray-500">
                        <MoreHorizontal className="w-5 h-5" />
                      </Button>
                    </div>

                    {/* Post Content */}
                    <div className="mb-4">
                      <p className="text-gray-200 whitespace-pre-line">{post.content}</p>
                    </div>

                    {/* Job Badge if it's a job post */}
                    {post.post_type === 'job' && (
                      <div className="bg-blue-600/10 border border-blue-600/30 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Briefcase className="w-5 h-5 text-blue-400" />
                          <span className="font-semibold text-blue-400">{post.job_title}</span>
                        </div>
                        <div className="flex flex-wrap gap-3 text-sm text-gray-400">
                          {post.job_location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {post.job_location}
                            </span>
                          )}
                          {post.job_type && (
                            <Badge variant="secondary" className="bg-zinc-800">
                              {post.job_type === 'remote' ? 'Remoto' : post.job_type === 'onsite' ? 'Presencial' : 'Híbrido'}
                            </Badge>
                          )}
                          {post.job_salary && (
                            <span className="text-green-400">{post.job_salary}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Post Stats */}
                    {(post.likes_count > 0 || post.comments_count > 0) && (
                      <div className="flex items-center justify-between py-2 border-t border-zinc-800 text-sm text-gray-500">
                        {post.likes_count > 0 && (
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="w-4 h-4 text-blue-500" />
                            {post.likes_count}
                          </span>
                        )}
                        {post.comments_count > 0 && (
                          <span>{post.comments_count} comentários</span>
                        )}
                      </div>
                    )}

                    {/* Post Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className={`flex-1 ${post.is_liked ? 'text-blue-500' : 'text-gray-400'} hover:text-blue-400`}
                        onClick={() => handleLike(post.id)}
                        data-testid={`like-btn-${post.id}`}
                      >
                        <ThumbsUp className={`w-5 h-5 mr-2 ${post.is_liked ? 'fill-current' : ''}`} />
                        Curtir
                      </Button>
                      <Button variant="ghost" size="sm" className="flex-1 text-gray-400 hover:text-blue-400">
                        <MessageCircle className="w-5 h-5 mr-2" />
                        Comentar
                      </Button>
                      <Button variant="ghost" size="sm" className="flex-1 text-gray-400 hover:text-blue-400">
                        <Share2 className="w-5 h-5 mr-2" />
                        Compartilhar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeedPage;
